import { Resident } from '@/types/resources/resident';
import { units } from '../resources/property';
import dayjs from 'dayjs';

// Celebrity names for residents
const celebrityNames = [
  'Tom Hanks', 'Julia Roberts', 'Brad Pitt', 'Angelina Jolie', 'Leonardo DiCaprio',
  'Jennifer Lawrence', 'Johnny Depp', 'Scarlett Johansson', 'Will Smith', 'Emma Stone',
  'Robert Downey Jr.', 'Meryl Streep', 'Denzel Washington', 'Sandra Bullock', 'Tom Cruise',
  'Nicole Kidman', 'George Clooney', 'Charlize Theron', 'Matt Damon', 'Cate Blanchett',
  'Ryan Reynolds', 'Blake Lively', 'Chris Hemsworth', 'Margot Robbie', 'Dwayne Johnson',
  'Gal Gadot', 'Hugh Jackman', 'Anne Hathaway', 'Daniel Radcliffe', 'Emma Watson',
  'Benedict Cumberbatch', 'Keira Knightley', 'Jake Gyllenhaal', 'Amy Adams', 'Chris Evans',
  'Mark Ruffalo', 'Jeremy Renner', 'Chris Pratt', 'Zoe Saldana', 'Vin Diesel', 'Paul Walker',
  'Michelle Rodriguez', 'Tyrese Gibson', 'Ludacris', 'Jason Statham', 'Kurt Russell', 'Helen Mirren',
  'Morgan Freeman', 'Al Pacino', 'Robert De Niro', 'Jack Nicholson', 'Diane Keaton',
  'Glenn Close', 'Judi Dench', 'Maggie Smith', 'Ian McKellen', 'Patrick Stewart',
  'Sean Connery', 'Pierce Brosnan', 'Daniel Craig', 'Harrison Ford', 'Mark Hamill',
  'Carrie Fisher', 'Billy Dee Williams', 'Anthony Daniels', 'Kenny Baker', 'Peter Mayhew',
  'David Prowse', 'James Earl Jones', 'Frank Oz', 'Alec Guinness', 'Peter Cushing',
  'Christopher Lee', 'Ian McDiarmid', 'Samuel L. Jackson', 'Natalie Portman', 'Ewan McGregor',
  'Liam Neeson', 'Hayden Christensen', 'Jake Lloyd', 'Ahmed Best', 'Ray Park',
  'Temuera Morrison', 'Daniel Logan', 'Orlando Bloom', 'Geoffrey Rush', 'Bill Nighy',
  'Stellan Skarsgård', 'Tom Hollander', 'Jack Davenport', 'Kevin McNally', 'Lee Arenberg',
  'Mackenzie Crook', 'Martin Freeman', 'Ian Holm', 'Elijah Wood', 'Sean Astin',
  'Dominic Monaghan', 'Billy Boyd', 'Viggo Mortensen', 'Sean Bean', 'John Rhys-Davies',
  'Liv Tyler', 'Hugo Weaving', 'Andy Serkis', 'Bernard Hill', 'David Wenham',
  'Karl Urban', 'Miranda Otto', 'Brad Dourif', 'John Noble', 'Bruce Spence',
  'Lawrence Makoare', 'Craig Parker', 'Harry Potter', 'Hermione Granger', 'Ron Weasley',
  'Albus Dumbledore', 'Severus Snape', 'Minerva McGonagall', 'Rubeus Hagrid', 'Sirius Black',
  'Remus Lupin', 'Neville Longbottom', 'Luna Lovegood', 'Ginny Weasley', 'Fred Weasley',
  'George Weasley', 'Percy Weasley', 'Bill Weasley', 'Charlie Weasley', 'Molly Weasley',
  'Arthur Weasley', 'Draco Malfoy', 'Lucius Malfoy', 'Narcissa Malfoy', 'Bellatrix Lestrange',
  'Voldemort', 'Peter Pettigrew', 'James Potter', 'Lily Potter', 'Cedric Diggory',
  'Cho Chang', 'Fleur Delacour', 'Viktor Krum', 'Dolores Umbridge', 'Cornelius Fudge',
  'Rufus Scrimgeour', 'Kingsley Shacklebolt', 'Nymphadora Tonks', 'Mad-Eye Moody', 'Barty Crouch Jr.',
  'Ludo Bagman', 'Rita Skeeter', 'Gilderoy Lockhart', 'Quirinus Quirrell', 'Horace Slughorn',
  'Sybill Trelawney', 'Aurora Sinistra', 'Pomona Sprout', 'Filius Flitwick', 'Argus Filch',
  'Poppy Pomfrey', 'Irma Pince', 'Rolanda Hooch', 'Wilhelmina Grubbly-Plank', 'Septima Vector',
  'Bathsheda Babbling', 'Charity Burbage', 'Fang', 'Buckbeak', 'Fawkes', 'Hedwig',
  'Crookshanks', 'Scabbers', 'Trevor', 'Nagini', 'Aragog', 'Fluffy', 'Norbert'
];

// Create 150 residents with celebrity names and proper unit assignments
export const residents: Resident[] = Array.from({ length: 150 }, (_, idx) => {
  // Use modulo to cycle through units, ensuring we don't exceed the units array
  const unit = units[idx % units.length];
  const celebrityName = celebrityNames[idx % celebrityNames.length];
  
  return {
    id: `RES-${(idx + 1).toString().padStart(3, '0')}`,
    user: {
      id: `user-res-${idx + 1}`,
      displayName: celebrityName,
      phoneNumber: `+91${9000000000 + idx}`
    },
    type: idx % 3 === 0 ? 'owner' : idx % 3 === 1 ? 'tenant' : 'family_member',
    status: idx % 10 === 0 ? 'inactive' : 'active', // 10% inactive
    moveInDate: dayjs().subtract(Math.floor(Math.random() * 365), 'days').toDate(),
    moveOutDate: idx % 10 === 0 ? dayjs().subtract(Math.floor(Math.random() * 30), 'days').toDate() : undefined,
    apartmentUnit: { id: unit.id, number: unit.number },
    block: unit.block,
    documents: [],
    contacts: [
      {
        name: `Emergency Contact for ${celebrityName}`,
        phone: `+91${9000000000 + idx + 1000}`,
        email: `emergency.${celebrityName.toLowerCase().replace(' ', '.')}@demo.com`
      }
    ],
    notes: `Demo resident ${idx + 1} - ${celebrityName} living in ${unit.block?.name} Unit ${unit.number}`,
    workspace: unit.workspace,
    activityRecords: [{
      timestamp: dayjs().subtract(Math.floor(Math.random() * 30), 'days').toDate(),
      type: 'created',
      performedBy: { id: 'admin-001', displayName: 'Property Manager' }
    }]
  };
});

// Extended resident data for detailed views
export const residentDetails = residents.map(resident => ({
  ...resident,
  paymentStatus: 'paid',
  lastPayment: {
    amount: 2500,
    date: dayjs('2024-03-01').toDate(),
    method: 'bank_transfer'
  },
  maintenanceRequests: [
    {
      id: 'MREQ001',
      title: 'Fix leaking faucet',
      status: 'completed',
      createdAt: dayjs('2024-03-05').toDate()
    }
  ],
  violations: [
    {
      id: 'VIO001',
      description: 'Noise complaint',
      date: dayjs('2024-02-15').toDate(),
      status: 'resolved'
    }
  ]
})); 